'use client';

import { Columns2, Ellipsis, LayoutGrid, Plus } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type { InferV1OfflineAvatar } from '@/app/apis';
import { InfoIcon } from '@/app/components/icons/InfoIcon';
import { Loading } from '@/app/components/icons/Loading';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { CoverflowCarousel } from '@/app/components/ui/coverflow-carousel';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { useDevice, useIsScreen } from '@/app/hooks';
import { cn } from '@/app/utils';
import { StudioTabs } from './studio-tabs';

export interface StudioAvatarItem {
  id: string;
  src: string;
  name: string;
  avatarId?: string;
  description?: string;
  voiceId?: string;
}

export const toStudioAvatarItem = (avatar: InferV1OfflineAvatar): StudioAvatarItem => ({
  id: avatar.id,
  src: avatar.live_image,
  name: avatar.name,
  avatarId: avatar.id,
  description: avatar.description,
  voiceId: avatar.voice_id,
});

interface AvatarGalleryProps {
  isLoggedIn: boolean;
  isUserInfoInitialized: boolean;
  selectedId: string | null;
  /** 内容区可用高度（px）。给了就按高度收缩卡片，保证 tab + 卡片一屏放下 */
  maxCardHeight?: number;
  officialAvatars: StudioAvatarItem[];
  officialAvatarsLoading: boolean;
  userAvatars: StudioAvatarItem[];
  userAvatarsLoading: boolean;
  onSelect: (avatar: StudioAvatarItem) => void;
  onCreateAvatar: () => void;
  onLogin: () => void;
  onEditAvatar?: (avatar: StudioAvatarItem) => void;
  onDeleteAvatar?: (avatar: StudioAvatarItem) => void;
  /** 选定角色并进入下一步 */
  onConfirm: (avatar: StudioAvatarItem) => void;
}

type ViewMode = 'gallery' | 'grid';

const GRID_LAYOUT_CLASS =
  'grid grid-cols-6 gap-6 py-8 sm:grid-cols-2 sm:gap-4 sm:py-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7';

const VIEW_MODES = [
  { value: 'gallery', icon: Columns2, labelKey: 'avatar.viewGallery' },
  { value: 'grid', icon: LayoutGrid, labelKey: 'avatar.viewGrid' },
] as const;

export function AvatarGallery({
  isLoggedIn,
  isUserInfoInitialized,
  selectedId,
  maxCardHeight,
  officialAvatars,
  officialAvatarsLoading,
  userAvatars,
  userAvatarsLoading,
  onSelect,
  onCreateAvatar,
  onLogin,
  onEditAvatar,
  onDeleteAvatar,
  onConfirm,
}: AvatarGalleryProps) {
  const t = useTranslations('tools.offlineDigitalHuman.studio');
  const tGlobal = useTranslations();
  const [tab, setTab] = useState<'official' | 'mine'>('official');
  const [preferredView, setView] = useState<ViewMode>('gallery');
  // 小屏只保留平铺视图（最少一行 2 个），不提供画廊视图
  const { isSmScreen } = useIsScreen();
  const { supportsHover } = useDevice();
  const view: ViewMode = isSmScreen ? 'grid' : preferredView;
  // 卡片 3:4：用可用高度减去 tab 栏（约 52px）和轮播上下留白（上下各 36px），换算出能放下的卡片宽度，
  // 让卡片填满这一屏、底下不留大空白；同时不超过屏幕宽度的 28%，最大 420px
  const fitWidth = maxCardHeight ? Math.max(132, Math.floor(((maxCardHeight - 128) * 3) / 4)) : undefined;
  const cardWidth = fitWidth ? `min(28vw, 420px, ${fitWidth}px)` : 'clamp(176px, 21vw, 300px)';
  const slides = officialAvatars.map((avatar, index) => ({
    src: avatar.src,
    alt: avatar.name || t('avatar.name', { number: index + 1 }),
    title: avatar.name || t('avatar.name', { number: index + 1 }),
  }));
  return (
    <section aria-label={t('steps.avatar.title')} className="flex h-full min-h-0 flex-col">
      <StudioTabs
        value={tab}
        onChange={setTab}
        options={[
          { value: 'official', label: t('avatar.official') },
          {
            value: 'mine',
            label: t('avatar.mine'),
            accessory: (
              <CustomTooltip
                content={t('avatar.mineOfflineHint')}
                trigger={supportsHover ? 'hover' : 'click'}
                side="top"
                sideOffset={8}
              >
                <button
                  type="button"
                  aria-label={t('avatar.mineOfflineHint')}
                  title={t('avatar.mineOfflineHint')}
                  className="flex size-5 shrink-0 items-center justify-center rounded-full text-system-white48 transition-colors hover:text-system-white80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01"
                >
                  <InfoIcon className="size-4" aria-hidden="true" />
                </button>
              </CustomTooltip>
            ),
          },
        ]}
        extra={
          <button
            type="button"
            onClick={onCreateAvatar}
            aria-label={t('avatar.newAvatar')}
            className="flex shrink-0 items-center gap-1 py-3 text-sm text-system-white64 transition-colors hover:text-system-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01"
          >
            <Plus className="size-4" aria-hidden="true" />
            <span className="sm:hidden">{t('avatar.newAvatar')}</span>
          </button>
        }
        trailing={
          tab === 'official' && !isSmScreen ? (
            <div className="flex items-center gap-1 sm:hidden">
              {VIEW_MODES.map(({ value, icon: Icon, labelKey }) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={view === value}
                  aria-label={t(labelKey)}
                  title={t(labelKey)}
                  onClick={() => setView(value)}
                  className={cn(
                    'flex size-8 items-center justify-center rounded-8 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01',
                    view === value
                      ? 'bg-system-white08 text-system-white'
                      : 'text-system-white48 hover:text-system-white80',
                  )}
                >
                  <Icon className="size-4" aria-hidden="true" />
                </button>
              ))}
            </div>
          ) : null
        }
        className="shrink-0"
      />
      <div
        data-avatar-scroll
        className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tab === 'official' && !isUserInfoInitialized ? (
          <div className="relative h-full min-h-[240px]">
            <GlobalLoader className="top-1/2" />
          </div>
        ) : null}
        {tab === 'official' && isUserInfoInitialized && !isLoggedIn ? (
          <div className="flex h-full min-h-[240px] items-center justify-center">
            <button
              type="button"
              onClick={onLogin}
              className="inline-flex h-12 min-w-32 items-center justify-center rounded-full bg-white px-8 font-semibold text-black text-sm transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 focus-visible:outline-offset-2"
            >
              {tGlobal('header.navLogin')}
            </button>
          </div>
        ) : null}
        {tab === 'official' && isUserInfoInitialized && isLoggedIn && officialAvatarsLoading ? (
          <div className="relative h-full min-h-0">
            <GlobalLoader className="top-1/2" />
          </div>
        ) : null}
        {tab === 'official' && isUserInfoInitialized && isLoggedIn && !officialAvatarsLoading && view === 'gallery' ? (
          <div className="flex h-full min-h-0 items-center">
            <CoverflowCarousel
              slides={slides}
              label={t('avatar.official')}
              previousLabel={t('avatar.previous')}
              nextLabel={t('avatar.next')}
              cardWidth={cardWidth}
              cardAspect={4 / 3}
              rotate={50}
              depth={0.9}
              shrink={0.82}
              blur={0.8}
              shade={0.22}
              showNavigation
              showCardTitle
              renderCardAction={(index) => {
                const avatar = officialAvatars[index];
                return avatar ? (
                  <button
                    type="button"
                    onClick={() => onConfirm(avatar)}
                    className="flex h-10 w-full items-center justify-center whitespace-nowrap rounded-full bg-white px-3 font-medium text-black text-sm transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 focus-visible:outline-offset-2"
                  >
                    {t(selectedId === avatar.id ? 'avatar.chosen' : 'avatar.choose')}
                  </button>
                ) : null;
              }}
              className="text-system-white"
              cardClassName="rounded-[28px] border-2 border-system-white20 bg-system-white04"
            />
          </div>
        ) : null}
        {tab === 'official' && isUserInfoInitialized && isLoggedIn && !officialAvatarsLoading && view === 'grid' ? (
          <div data-avatar-grid="official" className={GRID_LAYOUT_CLASS}>
            {officialAvatars.map((avatar, index) => (
              <AvatarTile
                key={avatar.id}
                src={avatar.src}
                label={avatar.name || t('avatar.name', { number: index + 1 })}
                selected={selectedId === avatar.id}
                onSelect={() => onSelect(avatar)}
                onConfirm={() => onConfirm(avatar)}
                confirmLabel={t(selectedId === avatar.id ? 'avatar.chosen' : 'avatar.choose')}
              />
            ))}
          </div>
        ) : null}
        {tab === 'mine' ? (
          <div data-avatar-grid="mine" className={GRID_LAYOUT_CLASS}>
            <button
              type="button"
              onClick={onCreateAvatar}
              className="group flex aspect-[3/4] w-full flex-col items-center justify-center gap-3 rounded-16 border border-system-white24 border-dashed bg-system-white02 px-2 text-center transition-colors hover:border-system-blue01 hover:bg-system-blue01/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 focus-visible:outline-offset-2 motion-reduce:transition-none"
            >
              <span className="flex size-10 items-center justify-center rounded-full bg-system-white08 text-system-white80">
                <Plus className="size-5" aria-hidden="true" />
              </span>
              <span className="font-medium text-sm text-system-white">{t('avatar.newAvatar')}</span>
            </button>
            {userAvatarsLoading ? (
              <div className="col-span-full flex min-h-[213px] items-center justify-center text-system-white48 sm:min-h-[160px]">
                <Loading className="size-6 animate-spin" />
              </div>
            ) : (
              userAvatars.map((avatar) => (
                <div key={avatar.id} className="w-full">
                  <AvatarTile
                    src={avatar.src}
                    label={avatar.name}
                    selected={selectedId === avatar.id}
                    onSelect={() => onSelect(avatar)}
                    onConfirm={() => onConfirm(avatar)}
                    confirmLabel={t(selectedId === avatar.id ? 'avatar.chosen' : 'avatar.choose')}
                    actions={
                      onEditAvatar && onDeleteAvatar ? (
                        <AvatarActions
                          avatar={avatar}
                          onEdit={() => onEditAvatar(avatar)}
                          onDelete={() => onDeleteAvatar(avatar)}
                        />
                      ) : undefined
                    }
                  />
                </div>
              ))
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** 平铺视图的角色卡：点图片选中；名字下方的幽灵按钮「选择」并进入下一步 */
function AvatarTile({
  src,
  label,
  selected,
  onSelect,
  onConfirm,
  confirmLabel,
  actions,
}: {
  src: string;
  label: string;
  selected: boolean;
  onSelect: () => void;
  onConfirm?: () => void;
  confirmLabel?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'group relative w-full overflow-hidden rounded-16 border-2 bg-system-white04 transition-colors motion-reduce:transition-none',
        selected ? 'border-system-blue01' : 'border-system-white20 hover:border-system-white48',
      )}
    >
      {actions ? <div className="absolute top-2 right-2 z-10">{actions}</div> : null}
      <button
        type="button"
        aria-label={label}
        aria-pressed={selected}
        onClick={onSelect}
        className="focus-visible:-outline-offset-2 block w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01"
      >
        <img src={src} alt="" loading="lazy" className="aspect-[3/4] w-full object-cover object-top" />
      </button>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center gap-2 bg-gradient-to-t from-black/85 via-black/45 to-transparent px-3 pt-12 pb-3 text-center">
        <span className="w-full truncate text-white text-xs">{label}</span>
        {onConfirm ? (
          <button
            type="button"
            onClick={onConfirm}
            className="pointer-events-auto flex h-8 items-center justify-center rounded-full bg-white/[0.16] px-5 text-white text-xs backdrop-blur-[8px] transition-colors hover:bg-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01"
          >
            {confirmLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function AvatarActions({
  avatar,
  onEdit,
  onDelete,
}: {
  avatar: StudioAvatarItem;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const t = useTranslations('tools.offlineAvatar.avatar');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('moreActions', { name: avatar.name })}
          className="flex size-8 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur transition-colors hover:bg-black/80"
        >
          <Ellipsis className="size-4" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>{t('edit')}</DropdownMenuItem>
        <DropdownMenuItem onSelect={onDelete} className="text-red-400">
          {t('delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
